import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../auth/AuthContext";
import { ApiError, apiGet } from "../api/client";
import { AuthLayout } from "../components/AuthLayout";

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const next = params.get("next") || "/learn";
  const justReset = params.get("reset") === "1";

  // Only offer a password reset when mail can actually be delivered —
  // otherwise the link promises an email that is only written to the log.
  const { data: health } = useQuery({
    queryKey: ["health"],
    queryFn: () => apiGet<{ email_enabled: boolean }>("/api/health"),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(username, password);
      const dest = user.role === "instructor" ? "/tutor"
        : user.role === "admin" ? "/admin"
        : next;
      navigate(dest, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Login failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthLayout
      title="Sign in"
      footer={
        <>
          {health?.email_enabled && (
            <Link to="/forgot-password">Forgot your password?</Link>
          )}
          <span className="auth-footer-alt">
            New here? <Link to="/register">Create an account</Link>
          </span>
          <span className="auth-footer-alt">
            Teaching? <Link to="/teach">Apply to teach</Link>
          </span>
        </>
      }
    >
      {justReset && (
        <div className="notice">
          Password updated. Sign in with your new password.
        </div>
      )}
      <form onSubmit={handleSubmit} className="auth-form">
        {error && (
          <div className="error" role="alert">
            {error}
          </div>
        )}
        <label>
          <span className="field-label">Username</span>
          <input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            autoFocus
            required
          />
        </label>
        <label>
          <span className="field-label">Password</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />
        </label>
        <button type="submit" className="btn btn-primary btn-lg" disabled={loading}>
          {loading ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </AuthLayout>
  );
}
